/**
 * Cost of goods helpers shared by order creation, line item updates,
 * the admin cascade update, and the sales cost report.
 *
 * Both line items and orders carry a single grouped `costOfGoods` object rather
 * than loose sibling fields, so the value and its audit trail stay together:
 *
 *   line item  → { current, original, overridden, updatedAt, updatedBy }
 *   order      → { total, partial, overridden, updatedAt, updatedBy }
 *
 * `current` is the per-unit cost in effect; `original` is what was first recorded
 * at order creation, preserved so a correction never loses the initial figure.
 *
 * The field is `null` when no cost was recorded — orders created before a brand
 * opted in must report as "N/A" rather than implying a zero-cost sale.
 *
 * An order's `total` only ever covers the line items that carry a cost, so
 * `partial` marks the case where that is less than the whole order. Never treat
 * a `partial` total as the order's cost without also excluding the revenue of
 * the lines it leaves out.
 */

import { firestore, doc, getDoc } from "@/firebase/init";
import { logWorkflowWarning } from "@/plugins/sentry";

// Number(null) and Number("") both coerce to 0, which would turn an untracked
// line item into a genuine $0 cost — guard those before coercing.
const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Per-unit cost in effect for a line item, or null when none was recorded.
 * Tolerates the flat number shape written during early development.
 */
export const unitCostOfGoods = (lineItem) => {
  const field = lineItem?.costOfGoods;
  if (field === null || field === undefined) return null;
  if (typeof field === "object") return toNumber(field.current);
  return toNumber(field);
};

/**
 * Per-unit cost originally recorded at order creation, before any correction.
 */
export const originalCostOfGoods = (lineItem) => {
  const field = lineItem?.costOfGoods;
  if (field === null || field === undefined) return null;
  if (typeof field === "object") {
    return toNumber(field.original) ?? toNumber(field.current);
  }
  return toNumber(field);
};

/** True when a line item carries a usable recorded cost. */
export const hasCostOfGoods = (lineItem) => unitCostOfGoods(lineItem) !== null;

/** True when a line item's cost was manually corrected after the fact. */
export const isCostOfGoodsOverridden = (lineItem) =>
  lineItem?.costOfGoods?.overridden === true;

/**
 * Extended cost for a single line item: unit cost × quantity.
 * Returns null when the line item has no recorded cost.
 */
export const lineCostOfGoods = (lineItem) => {
  const cost = unitCostOfGoods(lineItem);
  if (cost === null) return null;
  return cost * (toNumber(lineItem?.qty) ?? 0);
};

/**
 * Order-level total across line items. Returns null when no line item carries a
 * cost, so untracked orders are distinguishable from genuinely zero-cost ones.
 * Lines without a cost contribute nothing to a partially-tracked order.
 */
export const calculateCostOfGoodsTotal = (lineItems) => {
  if (!Array.isArray(lineItems) || !lineItems.some(hasCostOfGoods)) return null;

  return lineItems.reduce((total, lineItem) => {
    return total + (lineCostOfGoods(lineItem) ?? 0);
  }, 0);
};

/**
 * True when an order carries a cost on some line items but not all, so its
 * total is a partial figure. Both an untracked order and a fully tracked one
 * are false — there is nothing missing from either.
 */
export const isPartiallyCosted = (lineItems) => {
  const items = Array.isArray(lineItems) ? lineItems : [];
  return (
    items.some(hasCostOfGoods) && items.some((item) => !hasCostOfGoods(item))
  );
};

/**
 * Builds a line item's cost object at order creation. Returns null when the
 * product has no cost, so the field stays absent rather than recording a zero.
 */
export const buildLineCostOfGoods = (unitCost) => {
  const cost = toNumber(unitCost);
  if (cost === null) return null;

  return {
    current: cost,
    original: cost,
    overridden: false,
    updatedAt: null,
    updatedBy: null,
  };
};

/**
 * Current cost of goods for the given product IDs, read fresh from Firestore.
 * Only the SKUs on the order are fetched — never the brand's full catalog.
 *
 * Returns an empty map when the brand hasn't opted into COGS tracking, so
 * callers write line items with a null cost that reports as "N/A" rather than
 * a genuine $0. `failed` is only ever true when the brand DOES track cost and
 * the read itself failed — an untracked brand has nothing to fail at.
 */
export const resolveBrandCostOfGoods = async (brandID, productIDs = []) => {
  const costByProduct = new Map();
  let cogsEnabled = false;
  const uniqueProductIDs = [
    ...new Set((productIDs ?? []).filter((id) => typeof id === "string" && id)),
  ];

  try {
    const brandResponse = await getDoc(doc(firestore, "companyBrands", brandID));
    cogsEnabled =
      brandResponse.exists() && brandResponse.data().cogsEnabled === true;
    if (!cogsEnabled) return { costByProduct, cogsEnabled, failed: false };
    if (!uniqueProductIDs.length) {
      return { costByProduct, cogsEnabled, failed: false };
    }

    const productResponses = await Promise.all(
      uniqueProductIDs.map((productID) =>
        getDoc(doc(firestore, "products", productID)),
      ),
    );
    productResponses.forEach((productResponse, index) => {
      if (!productResponse.exists()) return;
      const cost = productResponse.data().costOfGoods;
      // "" would coerce to a genuine $0; only a real number is a cost
      if (cost === null || cost === undefined || cost === "") return;
      const parsed = Number(cost);
      if (Number.isFinite(parsed)) {
        costByProduct.set(uniqueProductIDs[index], parsed);
      }
    });
    return { costByProduct, cogsEnabled, failed: false };
  } catch (error) {
    // Report to Sentry as a warning (not just console.error) so a prod failure
    // here isn't silent — the order can still submit, but without a frozen cost.
    logWorkflowWarning("resolveBrandCostOfGoods: failed to resolve product costs", {
      workflow: "sales-order",
      action: "resolve-cost-of-goods",
      context: { brandID, cogsEnabled, productCount: uniqueProductIDs.length },
    });
    console.error("Failed to resolve cost of goods for brand:", error);
    return { costByProduct, cogsEnabled, failed: cogsEnabled };
  }
};

/**
 * Applies an admin correction to a line item's cost, preserving whatever was
 * originally recorded. Works on line items that had no cost at all (backfill).
 */
export const overrideLineCostOfGoods = (lineItem, newCost, { updatedAt, updatedBy }) => {
  const cost = toNumber(newCost);
  if (cost === null) return lineItem?.costOfGoods ?? null;

  return {
    current: cost,
    original: originalCostOfGoods(lineItem),
    overridden: true,
    updatedAt,
    updatedBy,
  };
};

/**
 * Builds an order's cost object from its line items. Pass `override` when this
 * write is itself a correction; otherwise prior correction metadata is carried
 * forward so recalculating totals never erases the audit trail.
 */
export const buildOrderCostOfGoods = (
  lineItems,
  { override = null, existing = null } = {},
) => {
  const total = calculateCostOfGoodsTotal(lineItems);
  if (total === null) return null;

  const partial = isPartiallyCosted(lineItems);

  if (override) {
    return {
      total,
      partial,
      overridden: true,
      updatedAt: override.updatedAt,
      updatedBy: override.updatedBy,
    };
  }

  const previouslyOverridden =
    existing?.overridden === true ||
    (lineItems ?? []).some((lineItem) => isCostOfGoodsOverridden(lineItem));

  return {
    total,
    partial,
    overridden: previouslyOverridden,
    updatedAt: existing?.updatedAt ?? null,
    updatedBy: existing?.updatedBy ?? null,
  };
};

/**
 * Revenue for a single line item, matching the basis used across sales
 * reporting: the per-unit sale price actually charged × quantity.
 */
export const lineRevenue = (lineItem) =>
  (toNumber(lineItem?.amount) ?? 0) * (toNumber(lineItem?.qty) ?? 0);

/**
 * Revenue, cost and quantity across only the line items that carry a recorded
 * cost. Every figure covers the same set of lines, so `revenue - cogs` is always
 * the true profit on that set — a partially-tracked order can never report full
 * revenue against partial cost. All three are null when no line carries a cost.
 *
 * `costedLines` / `lines` say how much of the order those figures represent, so
 * a caller can disclose that it is showing part of a larger order.
 */
export const costedTotals = (lineItems) => {
  const items = Array.isArray(lineItems) ? lineItems : [];
  let revenue = 0;
  let cogs = null;
  let qty = 0;
  let costedLines = 0;

  items.forEach((lineItem) => {
    const cost = lineCostOfGoods(lineItem);
    if (cost === null) return;
    revenue += lineRevenue(lineItem);
    cogs = (cogs ?? 0) + cost;
    qty += toNumber(lineItem?.qty) ?? 0;
    costedLines += 1;
  });

  const counts = { costedLines, lines: items.length };
  if (cogs === null) {
    return { revenue: null, cogs: null, qty: null, ...counts };
  }
  return { revenue, cogs, qty, ...counts };
};
